import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSetAtom } from 'jotai'
import { modalStatusAtom, selectedAccountAtom } from 'store/atoms'
import { paymentSecurityModalContainerId } from 'const/modalContainerId'
import {
  AllAccountSummaryGetResponse,
  useAccountFeatures
} from 'const/syfApiQueries'
import isFeatureFlagEnabled from 'helpers/isFeatureFlagEnabled'
import { setDeepLinkStatus } from 'helpers/deepLinkAnalytics'
import deepLinkStorage from 'helpers/deepLinkStorage'
import getQueryParam from 'syf-js-utilities/formats/getQueryParam'
import routes from 'const/routes'

const FEATURE_KEY = 'payment-security'

type DeepLinkResolutionAction =
  | { action: 'wait' }
  | { action: 'fail' }
  | { action: 'success'; accountId: string }
  | { action: 'nextCandidate' }

interface ResolveDeepLinkPaymentSecurityParams {
  accessToPaymentSecurity: boolean | undefined
  candidateIndex: number
  isAccountFeaturesError: boolean
  isAccountFeaturesFetched: boolean
  isAllAccountSummaryFetched: boolean
  matchedAccounts: AllAccountSummaryGetResponse[]
}

interface UsePaymentSecurityDeepLinkResult {
  isDeepLinkResolving: boolean
}

interface HandleResolutionParams {
  resolution: DeepLinkResolutionAction
  onSuccess: (accountId: string) => void
  onFailure: () => void
  onNextCandidate: () => void
}

interface ResolveAndHandleParams extends ResolveDeepLinkPaymentSecurityParams {
  onSuccess: (accountId: string) => void
  onFailure: () => void
  onNextCandidate: () => void
}

const getStoredFeature = (): string | null =>
  deepLinkStorage.getFeature() ||
  getQueryParam('feature') ||
  getQueryParam('link')

const getStoredClient = (): string | null =>
  deepLinkStorage.getClient() || getQueryParam('clientId')

/*
 * Determines the next action to take in the payment-security deeplink flow.
 * Iterates through matched accounts until one with payment-security access is found.
 *
 * Error handling strategy:
 *  account-features error  → advance to next candidate (the account exists, just this lookup failed)
 *  no matched accounts     → fail immediately (no accounts match the clientId)
 *  feature flag disabled   → fail immediately (checked before calling resolveDeepLink)
 */
export const resolveDeepLinkPaymentSecurity = ({
  accessToPaymentSecurity,
  candidateIndex,
  isAccountFeaturesError,
  isAccountFeaturesFetched,
  isAllAccountSummaryFetched,
  matchedAccounts
}: ResolveDeepLinkPaymentSecurityParams): DeepLinkResolutionAction => {
  if (!isAllAccountSummaryFetched) return { action: 'wait' }

  if (!matchedAccounts.length) {
    return { action: 'fail' }
  }

  const candidateAccountId = matchedAccounts[candidateIndex]?.accountId
  if (!candidateAccountId) return { action: 'fail' }

  const advanceOrFail: DeepLinkResolutionAction =
    candidateIndex < matchedAccounts.length - 1
      ? { action: 'nextCandidate' }
      : { action: 'fail' }

  if (isAccountFeaturesError) return advanceOrFail

  if (!isAccountFeaturesFetched) return { action: 'wait' }

  if (accessToPaymentSecurity) {
    return { action: 'success', accountId: candidateAccountId }
  }

  return advanceOrFail
}

const handleDeepLinkResolution = ({
  resolution,
  onSuccess,
  onFailure,
  onNextCandidate
}: HandleResolutionParams): void => {
  switch (resolution.action) {
    case 'wait':
      break
    case 'nextCandidate':
      onNextCandidate()
      break
    case 'success':
      onSuccess(resolution.accountId)
      break
    default:
      onFailure()
  }
}

const resolveAndHandle = ({
  accessToPaymentSecurity,
  candidateIndex,
  isAccountFeaturesError,
  isAccountFeaturesFetched,
  isAllAccountSummaryFetched,
  matchedAccounts,
  onSuccess,
  onFailure,
  onNextCandidate
}: ResolveAndHandleParams): void => {
  const resolution = resolveDeepLinkPaymentSecurity({
    accessToPaymentSecurity,
    candidateIndex,
    isAccountFeaturesError,
    isAccountFeaturesFetched,
    isAllAccountSummaryFetched,
    matchedAccounts
  })

  handleDeepLinkResolution({
    resolution,
    onSuccess,
    onFailure,
    onNextCandidate
  })
}

const getMatchedAccounts = (
  allAccountSummaryData: AllAccountSummaryGetResponse[],
  deepLinkClient: string | null,
  isPaymentSecurityDeepLink: boolean
): AllAccountSummaryGetResponse[] => {
  if (!deepLinkClient || !isPaymentSecurityDeepLink) return []
  return allAccountSummaryData.filter(
    (account) =>
      account?.product?.clientId?.toLowerCase() === deepLinkClient.toLowerCase()
  )
}

const usePaymentSecurityDeepLink = (
  allAccountSummaryData: AllAccountSummaryGetResponse[],
  isAllAccountSummaryFetched: boolean,
  isTokenFetched: boolean
): UsePaymentSecurityDeepLinkResult => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const updateModalStatus = useSetAtom(modalStatusAtom)
  const setSelectedAccount = useSetAtom(selectedAccountAtom)
  const hasRun = useRef(false)

  const deepLinkClient = getStoredClient()
  const deepLinkFeature = getStoredFeature()
  const isPaymentSecurityDeepLink = deepLinkFeature === FEATURE_KEY
  const isOnAccountServices = pathname === routes.ACCOUNT_SERVICES
  const [candidateIndex, setCandidateIndex] = useState(0)
  const [isDeepLinkResolving, setIsDeepLinkResolving] = useState(
    isPaymentSecurityDeepLink && isOnAccountServices
  )

  const matchedAccounts = useMemo(
    () =>
      getMatchedAccounts(
        allAccountSummaryData,
        deepLinkClient,
        isPaymentSecurityDeepLink
      ),
    [allAccountSummaryData, deepLinkClient, isPaymentSecurityDeepLink]
  )

  const currentCandidateAccountId = matchedAccounts[candidateIndex]?.accountId

  const {
    data: accountFeatures,
    isError: isAccountFeaturesError,
    isFetched: isAccountFeaturesFetched
  } = useAccountFeatures(
    isPaymentSecurityDeepLink && isTokenFetched
      ? currentCandidateAccountId || ''
      : ''
  )

  const finishWithFailure = useCallback(() => {
    deepLinkStorage.clearAll()
    hasRun.current = true
    setDeepLinkStatus('failure')
    setIsDeepLinkResolving(false)
    // Navigate to dashboard on failure
    navigate(routes.HOME)
  }, [navigate])

  const finishWithSuccess = useCallback(
    (accountId: string) => {
      hasRun.current = true
      setDeepLinkStatus('success')
      setSelectedAccount(accountId)
      /*
       * requestAnimationFrame is required due to a bug in SlideInModalPortal:
       * The portal's useEffect sets postAnimationState=true even when the modal
       * DOM element hasn't committed yet (document.getElementById returns null).
       * Once postAnimationState is true, the slide-in animation never retries.
       * Delaying by one frame ensures the portal element exists in DOM before
       * isOpen becomes true, allowing the animation to fire correctly.
       */
      requestAnimationFrame(() => {
        updateModalStatus(
          'PaymentSecurity',
          paymentSecurityModalContainerId(accountId)
        )
      })
      deepLinkStorage.clearAll()
      setIsDeepLinkResolving(false)
    },
    [updateModalStatus, setSelectedAccount]
  )

  useEffect(() => {
    // Only process payment-security deeplink on the account-services route
    if (!isPaymentSecurityDeepLink || !isOnAccountServices) {
      setIsDeepLinkResolving(false)
      return
    }

    if (hasRun.current) return
    if (!isAllAccountSummaryFetched) return
    if (!matchedAccounts.length) {
      finishWithFailure()
      return
    }

    if (!isFeatureFlagEnabled('isPaymentSecurityEnabled')) {
      finishWithFailure()
      return
    }

    resolveAndHandle({
      accessToPaymentSecurity: accountFeatures?.accessToPaymentSecurity,
      candidateIndex,
      isAccountFeaturesError,
      isAccountFeaturesFetched,
      isAllAccountSummaryFetched,
      matchedAccounts,
      onSuccess: finishWithSuccess,
      onFailure: finishWithFailure,
      onNextCandidate: () =>
        setCandidateIndex((currentIndex) => currentIndex + 1)
    })
  }, [
    accountFeatures?.accessToPaymentSecurity,
    candidateIndex,
    isAccountFeaturesError,
    isAccountFeaturesFetched,
    isAllAccountSummaryFetched,
    isPaymentSecurityDeepLink,
    isOnAccountServices,
    matchedAccounts,
    navigate,
    setSelectedAccount,
    updateModalStatus,
    finishWithFailure,
    finishWithSuccess
  ])

  return { isDeepLinkResolving }
}

export default usePaymentSecurityDeepLink
